import { useRef } from 'react';
import Chart, { useChart } from 'src/components/chart';
import { useTheme } from '@mui/material/styles';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useLocales } from 'src/locales';
import { formatCurrency } from 'src/utils/format-currency';
import { formatNumber, fPercent } from 'src/utils/format-number';
import { Box } from '@mui/system';

type Props = {
    data: {
        date: string;
        ad_cost: number;
        savings: number;
        brand_traffic: number;
        blended_ctr: number;
    }[];
};

export default function ChartPerformanceBusinessOverview({ data }: Props) {
    const theme = useTheme();
    const { t } = useLocales();
    const chartRef = useRef<any>(null);
    const workspace = useCurrentWorkspace();
    const lang = localStorage.getItem('i18nextLng') || 'en';

    const labels = (data || []).map((point) => {
        if (!point.date) return '';
        const d = new Date(point.date);
        return isNaN(d.getTime()) ? point.date : d.toISOString().split('T')[0];
    });

    const Y_AXIS_MARGIN_MULTIPLIER = 1.3;

    // arrondir max sur un intervalle "propre"
    const roundUpToNiceInterval = (value: number): number => {
        if (value <= 0) return 1;
        const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
        const normalized = value / magnitude;
        let niceValue;
        if (normalized <= 1.2) niceValue = 1.2;
        else if (normalized <= 1.5) niceValue = 1.5;
        else if (normalized <= 2) niceValue = 2;
        else if (normalized <= 3) niceValue = 3;
        else if (normalized <= 5) niceValue = 5;
        else if (normalized <= 7) niceValue = 7;
        else niceValue = 10;
        return niceValue * magnitude;
    };

    // calcul du max stacké (ad_cost + savings)
    const calculateMaxAdCostSavings = (data: Props['data']): number => {
        const stackedValues = data.map((d) => (d.ad_cost || 0) + (d.savings || 0));
        const maxVal = Math.max(...stackedValues, 0);
        return roundUpToNiceInterval(maxVal * Y_AXIS_MARGIN_MULTIPLIER);
    };

    // calcul du max pour revenue_or_conversions
    const calculateMaxRevenueOrConversions = (data: Props['data']): number => {
        const values = data.map((d) => d.blended_ctr || 0);
        const maxVal = Math.max(...values, 0);
        return roundUpToNiceInterval(maxVal * Y_AXIS_MARGIN_MULTIPLIER);
    };

    // 1. calcule les max
    const maxBars = calculateMaxAdCostSavings(data); // ex: ~250k
    const maxRevenue = calculateMaxRevenueOrConversions(data); // ex: ~5.5M

    // 2. facteur de scaling
    const scaleFactor = maxBars / maxRevenue;

    // 3. applique le scaling à la courbe
    const series = [
        {
            name: "Brand Traffic",
            type: "line",
            data: data?.map((point) => point.brand_traffic) || [],
        },
        {
            name: t("dashboard.global.ad_cost"),
            type: "bar",
            data: data?.map((point) => point.ad_cost) || [],
        },
        {
            name: t("dashboard.global.savings"),
            type: "bar",
            data: data?.map((point) => point.savings) || [],
        },
        {
            name: t("dashboard.global.blended_ctr"),
            type: "line",
            data: data?.map((point) => (point.blended_ctr || 0) * scaleFactor) || [],
            customRealData: data?.map((point) => point.blended_ctr) || [], // 👉 garde les vraies valeurs
        },
    ];

    const chartOptions = useChart({
        plotOptions: {
            bar: {
                columnWidth: '50%',
                borderRadius: 0,
                dataLabels: { position: 'top' }
            }
        },
        chart: {
            events: {
                mounted: (chartContext: any) => {
                    try {
                        if (chartContext?.hideSeries) {
                            chartContext.hideSeries(t('dashboard.global.blended_ctr'));
                        }
                    } catch (e) {
                        console.warn("Impossible de cacher la série blended_ctr  :", e);
                    }
                }
            },
            stacked: true,
            animations: { enabled: true, speed: 500 },
            toolbar: { show: false },
        },
        fill: {
            type: ['solid', 'solid', 'solid', 'solid'],
            gradient: {
                shadeIntensity: 1,
                inverseColors: false,
                opacityFrom: 0.45,
                opacityTo: 0.05,
                stops: [20, 100, 100],
            },
        },
        stroke: { width: [3, 0, 0, 3], curve: 'smooth' },
        colors: [
            theme.palette.secondary.main, // Brand Traffic
            theme.palette.warning.light,  // Ad Cost
            theme.palette.success.light,  // Savings
            theme.palette.info.light,   // Revenue/Conversions
        ],
        labels,
        yaxis: [
            {
                // Brand Traffic
                title: { text: t('dashboard.brandTraffic.totalBrandTraffic') },
                labels: { formatter: (val: number) => formatNumber(val) },
                min: 0
            },
            {
                // Ad Cost + Savings (stackés)
                opposite: true,
                min: 0,
                max: maxBars,
                title: { text: `${t('dashboard.global.ad_cost')} / ${t('dashboard.global.savings')}` },
                labels: {
                    formatter: (val: number) =>
                        formatCurrency(val, workspace?.currency || 'USD', lang, true, 2),
                },
            },
        ],
        tooltip: {
            shared: true,
            y: {
                formatter: (value: number, { seriesIndex, dataPointIndex, w }: any) => {
                    const series = w?.config?.series?.[seriesIndex];
                    if (series?.name === t("dashboard.global.blended_ctr")) {
                        // 👉 utilise la vraie valeur
                        const realValue = series.customRealData?.[dataPointIndex] || value;
                        return fPercent(realValue);
                    }
                    if (series?.name === "Brand Traffic") {
                        return formatNumber(value);
                    }
                    return formatCurrency(
                        value,
                        workspace?.currency || 'USD',
                        lang,
                        true,
                        2
                    );
                },
            },
        },
    });


    return (
        <Box sx={{ minHeight: 0, width: '100%', height: '100%' }}>
            <Chart
                dir="ltr"
                type="line"
                height={'100%'}
                ref={chartRef}
                series={series}
                options={chartOptions}
                sx={{ mt: 3 }}
            />
        </Box>

    );
}
